'use client';

import { useTranslations } from 'next-intl';
import { Menu, Text } from '@terra/ui-design-system';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  ArrowDown02Icon,
  ArrowUp02Icon,
  Database01Icon,
  LinkSquare02Icon,
  FocusPointIcon,
  GitCompareIcon,
  Message01Icon,
  PaintBoardIcon,
  ReplayIcon,
  Tag01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import type { HttpRequest } from '../../store/proxy-store';
import { getHighlightHex, HIGHLIGHT_OPTIONS } from '../annotations/constants';

type HttpRequestActionsMenuContentProps = {
  request?: Pick<HttpRequest, 'id' | 'threadId' | 'source'>;
  iconSize: number;
  onSelect: (value: string, requestId?: string) => void;
  itemFontSize?: 'xs';
};

export function HttpRequestActionsMenuContent({
  request,
  iconSize,
  onSelect,
  itemFontSize,
}: HttpRequestActionsMenuContentProps) {
  const t = useTranslations();
  const showRelatedContext =
    request?.threadId &&
    (request.source === 'copilot' || request.source === 'neo');
  const itemGap = iconSize <= 12 ? '1.5' : '2';
  const itemPy = iconSize <= 12 ? '1' : undefined;
  const itemPx = iconSize <= 12 ? '2' : undefined;

  return (
    <Menu.ItemGroup>
      <Menu.Item
        value="add-to-copilot"
        fontSize={itemFontSize}
        py={itemPy}
        px={itemPx}
      >
        <HStack gap={itemGap}>
          <Message01Icon size={iconSize} />
          <Text fontSize="xs">{t('httpHistory.actions.addToCopilot')}</Text>
        </HStack>
      </Menu.Item>
      <Menu.Item
        value="track-request"
        fontSize={itemFontSize}
        py={itemPy}
        px={itemPx}
      >
        <HStack gap={itemGap}>
          <Database01Icon size={iconSize} />
          <Text fontSize="xs">{t('httpHistory.actions.trackRequest')}</Text>
        </HStack>
      </Menu.Item>
      <Menu.Item
        value="replay-request"
        fontSize={itemFontSize}
        py={itemPy}
        px={itemPx}
      >
        <HStack gap={itemGap}>
          <ReplayIcon size={iconSize} />
          <Text fontSize="xs">{t('httpHistory.actions.goToReplay')}</Text>
        </HStack>
      </Menu.Item>
      {showRelatedContext && (
        <Menu.Item
          value="open-related-context"
          fontSize={itemFontSize}
          py={itemPy}
          px={itemPx}
        >
          <HStack gap={itemGap}>
            <LinkSquare02Icon size={iconSize} />
            <Text fontSize="xs">
              {request.source === 'copilot'
                ? t('httpHistory.actions.openRelatedCopilotChat')
                : t('httpHistory.actions.openRelatedNeoExecution')}
            </Text>
          </HStack>
        </Menu.Item>
      )}
      <Menu.Item
        value="show-context"
        fontSize={itemFontSize}
        py={itemPy}
        px={itemPx}
      >
        <HStack gap={itemGap}>
          <FocusPointIcon size={iconSize} />
          <Text fontSize="xs">{t('httpHistory.actions.showContext')}</Text>
        </HStack>
      </Menu.Item>
      <Menu.Separator />
      <Menu.Root
        positioning={{ strategy: 'fixed', placement: 'right-start' }}
        onSelect={(details) => onSelect(details.value, request?.id)}
      >
        <Menu.TriggerItem fontSize={itemFontSize} py={itemPy} px={itemPx}>
          <HStack gap={itemGap} flexGrow={1}>
            <PaintBoardIcon size={iconSize} />
            <Text fontSize="xs">{t('httpHistory.actions.highlight')}</Text>
          </HStack>
          <Menu.Indicator>›</Menu.Indicator>
        </Menu.TriggerItem>
        <Menu.Positioner>
          <Menu.Content minW="140px" py="1">
            {HIGHLIGHT_OPTIONS.map((option) => (
              <Menu.Item
                key={option.color}
                value={`highlight-${option.color}`}
                fontSize={itemFontSize}
                py="1"
                px="2"
              >
                <HStack gap={itemGap} alignItems="center">
                  <Box
                    w={`${iconSize}px`}
                    h={`${iconSize}px`}
                    borderRadius="sm"
                    style={{ backgroundColor: getHighlightHex(option.color) }}
                    flexShrink={0}
                  />
                  <Text fontSize="xs">
                    {t(`httpHistory.actions.highlightColors.${option.color}`)}
                  </Text>
                </HStack>
              </Menu.Item>
            ))}
            <Menu.Separator />
            <Menu.Item
              value="highlight-clear"
              fontSize={itemFontSize}
              py="1"
              px="2"
            >
              <HStack gap={itemGap}>
                <Cancel01Icon size={iconSize} />
                <Text fontSize="xs">
                  {t('httpHistory.actions.clearHighlight')}
                </Text>
              </HStack>
            </Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>
      <Menu.Separator />
      <Menu.Item
        value="add-comment"
        fontSize={itemFontSize}
        py={itemPy}
        px={itemPx}
      >
        <HStack gap={itemGap}>
          <Message01Icon size={iconSize} />
          <Text fontSize="xs">{t('httpHistory.actions.addComment')}</Text>
        </HStack>
      </Menu.Item>
      <Menu.Item
        value="add-tags"
        fontSize={itemFontSize}
        py={itemPy}
        px={itemPx}
      >
        <HStack gap={itemGap}>
          <Tag01Icon size={iconSize} />
          <Text fontSize="xs">{t('httpHistory.actions.addTags')}</Text>
        </HStack>
      </Menu.Item>
      <Menu.Separator />
      <Menu.Root
        positioning={{ strategy: 'fixed', placement: 'right-start' }}
        onSelect={(details) => onSelect(details.value, request?.id)}
      >
        <Menu.TriggerItem fontSize={itemFontSize} py={itemPy} px={itemPx}>
          <HStack gap={itemGap} flexGrow={1}>
            <GitCompareIcon size={iconSize} />
            <Text fontSize="xs">{t('httpHistory.actions.sendToComparer')}</Text>
          </HStack>
          <Menu.Indicator>›</Menu.Indicator>
        </Menu.TriggerItem>
        <Menu.Positioner>
          <Menu.Content minW="140px" py="1">
            <Menu.Item
              value="send-to-comparer-request"
              fontSize={itemFontSize}
              py="1"
              px="2"
            >
              <HStack gap={itemGap}>
                <ArrowUp02Icon size={iconSize} />
                <Text fontSize="xs">{t('httpHistory.actions.request')}</Text>
              </HStack>
            </Menu.Item>
            <Menu.Item
              value="send-to-comparer-response"
              fontSize={itemFontSize}
              py="1"
              px="2"
            >
              <HStack gap={itemGap}>
                <ArrowDown02Icon size={iconSize} />
                <Text fontSize="xs">{t('httpHistory.actions.response')}</Text>
              </HStack>
            </Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>
    </Menu.ItemGroup>
  );
}
